/**
 * Availability / offline / format marker — TASK 8.1 Step 4 (mock defects 8 + 10).
 *
 * ONE component decides, for every item on every surface, WHICH of the three
 * per-item states applies and HOW it is rendered. The mock did neither: it
 * inlined the cascade in `_album()` (L1041) for track rows and a different one
 * in `_card()` (L1018) for cards, and the "not downloaded" ribbon existed on
 * only TWO of the six grids (ui-contract §10.6: "genre/favorites/search card
 * templates have no offDis span → dim-only, no badge"). Both inconsistencies
 * are fixed by routing every call site through {@link availabilityState} +
 * {@link AvailabilityBadge}.
 *
 * ── THE THREE STATES, IN PRECEDENCE ORDER ─────────────────────────────────
 * The order is the mock's own cascade (`gone ? … : bad ? … : off ? …`), and it
 * is the right one: "gone" is permanent, "format" is device-permanent,
 * "not downloaded" is transient.
 *
 *   1. `unavailable`        `available === false` — the Drive file vanished
 *                           (AC 3.9 soft flag; the stream route answers 410).
 *   2. `format-unsupported` `formatSupported === false` — this device cannot
 *                           decode the container (AC 5.7).
 *   3. `not-downloaded`     offline AND the item is not in the local cache.
 *
 * ── SCOPE OF THE `unavailable` TREATMENT (dev-plan TASK 8.1 step 4, C1) ────
 * The "⊘ no longer available" TRACK marker belongs only to surfaces that
 * RETAIN a reference to a vanished track — playlists, favorites, downloads and
 * album-detail. Browse and search never show it, because TASK 4.1/4.2 already
 * EXCLUDE `available=false` tracks from those responses; a badge there would
 * mean the server broke its own contract. So {@link availabilityState} takes
 * the surface and refuses that combination — visibly, via `onScopeViolation`,
 * never by silently swallowing it.
 *
 * A whole `available=false` ALBUM is the retained exception (AC 3.9): it STAYS
 * in browse/search grids carrying the marker. That is why `kind` is part of
 * the decision and why the browse grids really do render this component's
 * `unavailable` branch in production (see views/browse/ArtistDetail.js).
 *
 * ── WHERE THE `offline` SIGNAL COMES FROM ─────────────────────────────────
 * `modules/app/index.js#startApp` reads `navigator.onLine` (the same signal
 * `lib/telemetry-client.ts` already uses), keeps it current via `online`/
 * `offline` window listeners, and passes it into the catalog views when they
 * are constructed — so a flip mid-view shows on the NEXT navigation, not live.
 *
 * The download set is REAL as of TASK 10.1 and is read from CACHE STORAGE, not
 * from the download records: a `done` record whose body iOS evicted under
 * storage pressure must not mark a track playable offline, and the cache is the
 * only thing that knows (see `lib/download-manager.js#downloadedTrackIds`).
 *
 * TRACK rows take the PER-TRACK set and album/grid cards take the per-album
 * roll-up, and the distinction is load-bearing in both directions. Feeding the
 * album roll-up to a track row marks nine cached tracks "not downloaded"
 * because a tenth failed; feeding a per-track fact to an album card marks the
 * whole album downloaded because one track of it is, turning the other nine
 * into silent stream failures offline — which is precisely the AC 13.5 defeat
 * this badge exists to prevent.
 *
 * §8.5: every state renders GLYPH + TEXT, never colour alone.
 *
 * @module
 */

import { el } from './primitives/dom.js';

/** @typedef {'unavailable'|'format-unsupported'|'not-downloaded'} AvailabilityState */

/**
 * Copy per state and per rendering variant, verbatim from ui-contract §10.6
 * ("Per-item offline (not-downloaded) vs. 410-gone — distinct markers" and
 * "Seeded edge-value markers"). Row copy is lower-case, card copy is the
 * upper-case ribbon; the glyphs differ per state so the three are told apart
 * by glyph + copy rather than by colour (they share the yellow pill).
 */
export const AVAILABILITY_COPY = Object.freeze({
  unavailable: Object.freeze({
    row: '⊘ no longer available',
    card: '⊘ NO LONGER AVAILABLE',
    /** The full-player banner (ui-contract §10.6 "Full player banners"). */
    banner: 'This track is no longer available (removed from Drive).',
  }),
  'format-unsupported': Object.freeze({
    row: 'format not supported on this device',
    card: null, // an ALBUM has no format — see `AvailabilityBadge`
    banner: 'Format not supported on this device.',
  }),
  'not-downloaded': Object.freeze({
    // U+2312 ARC on rows, U+2301 ELECTRIC ARROW on cards — the mock's own two
    // glyphs (L1041 vs L172), kept so the fidelity baseline is untouched.
    row: '⌒ not downloaded',
    card: '⌁ NOT DOWNLOADED',
    banner: 'Not downloaded — unavailable offline.',
  }),
});

/** Surfaces that RETAIN a reference to a vanished item and must mark it.
 *  `history` is the recently-played-tracks rail: dto.ts pins that a removed
 *  track KEEPS its place there, flagged (the AC 3.9 retain rule). */
export const RETAIN_SURFACES = Object.freeze([
  'album',
  'playlist',
  'favorites',
  'downloads',
  'history',
]);
/** Surfaces the server has already filtered (AC 3.9 / TASK 4.1-4.2). */
export const FILTERED_SURFACES = Object.freeze(['browse', 'search']);

/**
 * Decides which marker (if any) an item carries.
 *
 * @param {{
 *   available?: boolean,
 *   formatSupported?: boolean,
 *   offline?: boolean,
 *   downloaded?: boolean,
 *   kind?: 'track'|'album',
 *   surface?: 'browse'|'search'|'album'|'playlist'|'favorites'|'downloads'|'history',
 *   onScopeViolation?: (message: string) => void,
 * }} item
 *   `surface` defaults to `album` (a retain surface) so a caller that forgets
 *   it gets the SAFE behaviour — the marker shows — rather than a silent drop.
 * @returns {AvailabilityState|null}
 */
export function availabilityState(item) {
  const {
    available = true,
    formatSupported = true,
    offline = false,
    downloaded = true,
    kind = 'track',
    surface = 'album',
    onScopeViolation = defaultScopeViolation,
  } = item;

  if (available === false) {
    // The retained exception: a whole gone ALBUM keeps its marker everywhere.
    if (kind === 'album') return 'unavailable';
    if (FILTERED_SURFACES.includes(surface)) {
      // Not a silent drop: the server contract says this cannot happen, so if
      // it does, someone needs to hear about it.
      onScopeViolation(
        `AvailabilityBadge: an available=false TRACK reached the "${surface}" surface, ` +
          'which TASK 4.1/4.2 filters server-side. Rendering no marker (ui-contract §10.6 scope).',
      );
      return null;
    }
    return 'unavailable';
  }

  if (formatSupported === false) return 'format-unsupported';
  if (offline && !downloaded) return 'not-downloaded';
  return null;
}

/**
 * Fallback reporter, used only when no caller injected one.
 *
 * IT IS A FALLBACK, NOT THE DESIGN. What `onScopeViolation` reports is a
 * SERVER CONTRACT VIOLATION — TASK 4.1/4.2 promise to filter these rows out,
 * and reaching this branch means they did not. A breach of a server promise
 * that is reported only to the end user's devtools console is a breach nobody
 * on the team ever hears about, so both production call sites now inject
 * `startApp`'s diagnostics `warn`, which is the app's ONE outbound channel for
 * "something is wrong and it is not the user's fault".
 *
 * WHY NOT `lib/telemetry-client.ts` DIRECTLY: that client is a closed
 * allowlist of exactly five §11 event names, enforced at runtime by
 * `isTelemetryEventName`, and `POST /api/telemetry` does not exist yet
 * (TASK 11.1). Inventing a sixth name here would be refused by the client and
 * dropped, which is strictly worse than the console — it would LOOK reported.
 * Routing through the injected `warn` means TASK 11.1 has exactly one wire to
 * change, in `modules/app/index.js`, rather than a `console.warn` buried in a
 * leaf component that no caller can intercept.
 *
 * @param {string} message
 * @returns {void}
 */
function defaultScopeViolation(message) {
  console.warn(message);
}

/**
 * Builds the marker element for a state.
 *
 * @param {{ state: AvailabilityState, variant?: 'row'|'card' }} options
 * @returns {HTMLElement}
 */
export function AvailabilityBadge(options) {
  const { state, variant = 'row' } = options;
  const copy = AVAILABILITY_COPY[state];
  if (copy === undefined) {
    throw new Error(`AvailabilityBadge: unknown state "${String(state)}"`);
  }
  if (variant !== 'row' && variant !== 'card') {
    throw new Error(`AvailabilityBadge: unknown variant "${String(variant)}"`);
  }

  const text = copy[variant];
  if (text === null) {
    // `format-unsupported` on a COVER: an album card has no single format, so
    // there is no honest thing to print. Throwing beats inventing copy.
    throw new Error(`AvailabilityBadge: state "${state}" has no "${variant}" rendering`);
  }

  if (variant === 'card') {
    // mock L171/L262 — the cover ribbon; the offline one is the dimmer colour.
    return el('span', {
      className:
        state === 'not-downloaded'
          ? 'sm-cover__ribbon sm-cover__ribbon--offline'
          : 'sm-cover__ribbon',
      text,
    });
  }

  // mock L308 — the shared yellow track-row pill.
  return el('span', { className: 'sm-pill sm-pill--badge', text });
}

/**
 * The mock's per-item dimming (`{{ c.dim }}` / `{{ t.dim }}`, L1018/L1041):
 * `.55` for gone and for offline-not-downloaded, `1` otherwise. A
 * format-unsupported row is NOT dimmed in the mock — it stays legible.
 *
 * @param {AvailabilityState|null} state
 * @returns {number}
 */
export function availabilityOpacity(state) {
  return state === 'unavailable' || state === 'not-downloaded' ? 0.55 : 1;
}

/**
 * Whether the item can be tapped into playback. Every marked state except
 * "none" blocks it; the caller surfaces {@link AVAILABILITY_COPY} `banner`
 * copy instead of starting a stream that cannot work.
 *
 * @param {AvailabilityState|null} state
 * @returns {boolean}
 */
export function isPlayable(state) {
  return state === null;
}
